import { Loader } from "@googlemaps/js-api-loader";
import { Ref, forwardRef, useEffect, useImperativeHandle, useState } from "react";
import AsyncSelect from "react-select/async-creatable";
import { useDebouncedCallback } from "use-debounce";

import autocompletionRequestBuilder from "./autocompletionRequestBuilder";

const styles = (theme: string) => ({
  control: (styles: any) => ({
    ...styles,
    border: theme === "light" ? "1px solid #f2f2f2" : "1px solid #fff",
    background: theme === "light" ? "#f2f2f2" : "#fff",
    minHeight: 38,
    fontSize: 14,
    borderRadius: 10,
    transition: "background-color 0.2s ease-in-out, border-color 0.2s ease-in-out",
    "&:focus-within": {
      background: "#fff",
      border: "1px solid #4c9aff",
      boxShadow: "none"
    },
    "&:hover": {
      background: "#e9ebef",
      border: "1px solid #dee0e6"
    }
  }),
  placeholder: (defaultStyles: any) => ({
    ...defaultStyles,
    color: "#7a869a",
    fontSize: 14
  }),
  menu: (base: any) => ({ ...base, overflow: "hidden" }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }), //@ts-ignore
  option: (styles: any, { isDisabled, isSelected }: any) => ({
    ...styles,
    backgroundColor: isSelected ? "#deebff" : "#fff",
    color: isSelected ? "#0052cc" : "#333",
    borderLeftWidth: 2,
    borderColor: isSelected ? "#0052cc" : "#fff",
    cursor: isDisabled ? "not-allowed" : "default",
    "&:hover": {
      borderLeftWidth: 2,
      borderColor: "#0052cc",
      background: isSelected ? "#deebff" : "#f7f7f7"
    }
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "#666"
  })
});

interface AutocompletePlace {
  description: string;
  place_id: string;
}

interface GooglePlacesAutocompleteProps {
  apiKey?: string;
  apiOptions?: any;
  autocompletionRequest?: any;
  debounce?: number;
  minLengthAutocomplete?: number;
  selectProps?: any;
  onLoadFailed?: (error: Error) => void;
  withSessionToken?: boolean;
  theme?: string;
}

const GooglePlacesAutocomplete = forwardRef(
  (
    {
      apiKey = "",
      apiOptions = {},
      autocompletionRequest = {},
      debounce = 300,
      minLengthAutocomplete = 0,
      selectProps = {},
      onLoadFailed = console.error,
      withSessionToken = false,
      theme
    }: GooglePlacesAutocompleteProps,
    ref: Ref<any>
  ) => {
    const [placesService, setPlacesService] = useState<google.maps.places.AutocompleteService | undefined>(undefined);
    const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | undefined>(
      undefined
    );

    const fetchSuggestions = useDebouncedCallback((value: string, cb: (options: any[]) => void) => {
      if (!placesService) return cb([]);
      if (value.length < minLengthAutocomplete) return cb([]);

      const autocompletionReq = { ...autocompletionRequest };

      placesService?.getPlacePredictions(
        autocompletionRequestBuilder(autocompletionReq, value, withSessionToken && sessionToken),
        (
          suggestions: google.maps.places.AutocompletePrediction[] | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            cb(
              (suggestions || []).map((suggestion: google.maps.places.AutocompletePrediction) => ({
                label: suggestion?.description,
                value: suggestion
              }))
            );
          } else {
            // Handle error
            console.error(`PlacesServiceStatus: ${status}`);
            cb([]);
          }
        }
      );
    }, debounce);

    const initializeService = () => {
      if (!window.google) throw new Error("[react-google-places-autocomplete]: Google script not loaded");
      if (!window.google.maps) throw new Error("[react-google-places-autocomplete]: Google maps script not loaded");
      if (!window.google.maps.places)
        throw new Error("[react-google-places-autocomplete]: Google maps places script not loaded");

      setPlacesService(new window.google.maps.places.AutocompleteService());
      setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
    };

    useImperativeHandle(
      ref,
      () => ({
        getSessionToken: () => {
          return sessionToken;
        },
        refreshSessionToken: () => {
          setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
        }
      }),
      [sessionToken]
    );

    useEffect(() => {
      const init = async () => {
        try {
          if (!window.google || !window.google.maps || !window.google.maps.places) {
            await new Loader({
              apiKey,
              ...{ libraries: ["places"], ...apiOptions }
            }).load();
          }
          initializeService();
        } catch (error) {
          onLoadFailed(error as any);
        }
      };

      if (apiKey) init();
      else initializeService();
    }, [apiKey]);

    return (
      <AsyncSelect
        {...selectProps}
        loadOptions={fetchSuggestions}
        getOptionValue={({ value }: { value: AutocompletePlace }) => value.place_id}
        formatCreateLabel={(inputText: string) => `Add Address : "${inputText}"`}
        styles={theme && styles(theme)}
      />
    );
  }
);

export default GooglePlacesAutocomplete;
